import React, { useState, useRef, useEffect } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { toast } from 'sonner'
import { ArrowLeft, Printer, MessageCircle, Info, Download, ThumbsUp, UploadCloud, Paperclip, X as XIcon, Sparkles, Save, Clock, ShieldCheck, Percent } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import BrandLogo from '../components/BrandLogo'
import SmartBack from '../components/SmartBack'
import DictateButton from '../components/DictateButton'

const rand = (n) => `R${(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
// Local-midnight yyyy-mm-dd + a 5-years-back floor — matches the server invoice-date guard.
const isoDay = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
const todayISO = () => isoDay(new Date())
const minISO = () => { const d = new Date(); d.setFullYear(d.getFullYear() - 5); return isoDay(d) }

const editCls = 'w-full rounded-md border border-brand-border bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'
const EMPTY_LINE = { procedure_code_id: '', code: '', description: '', tooth_number: '', quantity: '1', fee: '' }

// Follow-up tracking card — the SAME fields as the Estimates tracker, editable right on the estimate
// (Paul 2026-07-01: "fill in those details for every estimate, from the patient's account"). Auto-saves
// (debounced) to /estimates/:id/tracking with a partial reload, so nothing is lost and there's no Save button.
function FollowUpCard({ estimate, reasons }) {
  const [reason, setReason] = useState(estimate.not_booked_reason || '')
  const [note, setNote] = useState(estimate.not_booked_reason_note || '')
  const [followUp, setFollowUp] = useState((estimate.next_follow_up_at || '').slice(0, 10))
  const [updateNote, setUpdateNote] = useState(estimate.follow_up_notes || '')
  const updateBox = useRef(null)
  const [knows, setKnows] = useState(estimate.patient_knows_next_treatment === true ? 'true' : estimate.patient_knows_next_treatment === false ? 'false' : '')
  const [savedAt, setSavedAt] = useState(null)
  const [saving, setSaving] = useState(false)
  const timer = useRef(null); const mounted = useRef(false)
  const save = () => {
    setSaving(true)
    router.patch(`/estimates/${estimate.id}/tracking`, {
      tracking: { not_booked_reason: reason, not_booked_reason_note: note, next_follow_up_at: followUp,
        follow_up_notes: updateNote, patient_knows_next_treatment: knows, logged_follow_up: true },
    }, { preserveScroll: true, preserveState: true, only: [ 'estimate' ],
      onSuccess: () => setSavedAt(new Date()),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not save'),
      onFinish: () => setSaving(false) })
  }
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return }
    clearTimeout(timer.current); timer.current = setTimeout(save, 800)
    return () => clearTimeout(timer.current)
  }, [ reason, note, followUp, updateNote, knows ])
  return (
    <div className="no-print mt-3 rounded-xl border border-brand-border bg-brand-surface/30 p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-brand-ink"><Clock size={14} /> Follow-up tracking</h3>
        <span className="text-xs font-medium" aria-live="polite">
          {saving ? <span className="text-brand-muted">Saving…</span> : savedAt ? <span className="text-emerald-600">✓ Saved automatically</span> : <span className="text-brand-muted">Changes save automatically</span>}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className="text-xs font-medium text-brand-muted md:col-span-2">Update note — last communication + procedure done + outstanding
          <textarea ref={updateBox} value={updateNote} onChange={(e) => setUpdateNote(e.target.value)} rows={2} placeholder="e.g. WA sent 26 Jun — fillings done, crowns outstanding, will book after bonus" className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm" />
          <DictateButton targetRef={updateBox} onText={setUpdateNote} context="estimate_note" className="mt-1" />
        </label>
        <label className="text-xs font-medium text-brand-muted">Patient knows the next treatment?
          <select value={knows} onChange={(e) => setKnows(e.target.value)} className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm text-brand-ink">
            <option value="">— not recorded —</option><option value="true">Yes</option><option value="false">No</option>
          </select>
        </label>
        <label className="text-xs font-medium text-brand-muted">Reason not booked
          <select value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm text-brand-ink">
            <option value="">— none —</option>
            {Object.entries(reasons).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
        </label>
        <label className="text-xs font-medium text-brand-muted">Reason detail
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. waiting for year-end bonus" className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs font-medium text-brand-muted">Next follow-up — when to contact them again
          <input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm" />
        </label>
      </div>
    </div>
  )
}

// Inline tariff-code editor for an EXISTING line — retype the code (the same way it's typed in the
// Quick-add box) and, on Enter or blur, resolve it against the SAME code catalogue to pull in the new
// treatment name + fee, KEEPING the tooth / visit / quantity (no delete-and-re-add). An unknown code
// shows a clear inline error and leaves the line unchanged — it is never wiped. (Paul 2026-07-22.)
function CodeEdit({ line, codes, onResolve, listId, disabled = false }) {
  const [val, setVal] = useState(line.code || '')
  const [err, setErr] = useState(false)
  const lastActed = useRef(line.code || '')
  useEffect(() => { setVal(line.code || ''); setErr(false); lastActed.current = line.code || '' }, [ line.code ])
  const commit = () => {
    const typed = (val || '').trim()
    if (!typed) { setVal(line.code || ''); setErr(false); return }                       // don't wipe a line to nothing
    if (typed.toUpperCase() === String(lastActed.current || '').toUpperCase()) { setErr(false); return } // no change / already sent
    const pc = codes.find((c) => String(c.code).toUpperCase() === typed.toUpperCase())
    if (!pc) { setErr(true); return }                                                    // unknown → error, leave line as-is
    setErr(false); lastActed.current = pc.code
    onResolve(line, pc)
  }
  return (
    <div>
      <input
        list={listId}
        value={val}
        disabled={disabled}
        onChange={(e) => { setVal(e.target.value); setErr(false) }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit() }
          else if (e.key === 'Escape') { e.preventDefault(); setVal(line.code || ''); setErr(false); e.currentTarget.blur() }
        }}
        onBlur={commit}
        title="Type a tariff code and press Enter to swap it — keeps the tooth, visit & quantity, and pulls in the new treatment name + fee"
        className={`w-20 rounded border px-1.5 py-0.5 font-mono text-xs focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:bg-brand-surface disabled:text-brand-muted ${err ? 'border-rose-400 ring-1 ring-rose-300' : 'border-brand-border focus:ring-brand-primary/40'}`}
      />
      {err && <p className="mt-0.5 text-[10px] font-medium text-rose-600">Unknown code</p>}
    </div>
  )
}

export default function EstimateShow({ estimate = {}, consent_enabled = false, scheme_rates_allowed = false, scheme_rates_preview = 0, practice = {}, patient = {}, attachments = [], procedure_codes = [], favourite_codes = [], treatment_macros = [], suggested_macros = [], visit_reason = null, providers = [], as_invoice = false, not_booked_reasons = {}, companion_suggestions = [], place_of_service_options = [], send_again = [] }) {
  const multiVisit = (estimate.visits || []).length > 1
  // LIVE-REFRESH: when this estimate's money-relevant content changes (a line added/removed/edited,
  // its status or title), flag the owning patient's page as stale so the Estimates/Account cards on
  // PatientShow re-sync silently the next time it's viewed (see PatientShow live-refresh). The
  // signature is derived from the current props, and the first render is skipped, so this fires only
  // on an ACTUAL change made here — never merely on mount.
  const estimateStaleSig = (estimate.visits || []).flatMap((v) => v.lines || [])
    .map((l) => `${l.id}:${l.unit_fee}:${l.quantity}:${l.code}`).join('|') + `#${estimate.status}#${estimate.title}`
  const estStaleFirst = useRef(true)
  useEffect(() => {
    if (estStaleFirst.current) { estStaleFirst.current = false; return }
    try { if (patient?.id) localStorage.setItem('patient_stale:' + patient.id, '1') } catch (e) {}
  }, [estimateStaleSig])
  // Dentists may only SAVE an estimate — never turn it into an invoice. Hide every invoice-creating
  // action (server also blocks the routes; see enforce_dentist_scope!). (Paul 2026-06-30.)
  const isDentist = usePage().props?.current_user?.role === 'dentist'
  const [accepting, setAccepting] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiBusy, setAiBusy] = useState(false)
  // In-place AI reviser (Paul 2026-07-25): ADJUSTS the existing lines, never appends (that's aiCompose).
  const [revisePrompt, setRevisePrompt] = useState('')
  const [reviseBusy, setReviseBusy] = useState(false)
  const [rereadBusy, setRereadBusy] = useState(false)
  const [noteDismissed, setNoteDismissed] = useState(false)
  // Controlled title so the explicit "Save" button can persist it (and it shows on screen, not
  // just on print). The estimate's lines already auto-save; Save is the reassurance that the whole
  // record — incl. the title — is kept WITHOUT creating an invoice (that's what "Accept" does).
  const [title, setTitle] = useState(estimate.title || '')
  // Re-sync when the server sets the title (e.g. the AI compose auto-names the estimate after this page
  // mounted) — without this the saved title stays invisible until a manual reload.
  useEffect(() => { setTitle(estimate.title || '') }, [estimate.title])
  const [saving, setSaving] = useState(false)
  const [sendingWa, setSendingWa] = useState(false)
  // Send this estimate to the patient on WhatsApp (approved PDF template — delivers outside the 24h
  // window). Deliberate button press only, with a confirm — never automatic (Paul 2026-07-01).
  // Stage 5 — consent rides on the estimate the patient is already being asked to accept, so
  // they sign for the actual plan (per visit, per tooth, per code) rather than for a total.
  const [issuingConsent, setIssuingConsent] = useState(false)
  const issueConsent = () => {
    setIssuingConsent(true)
    router.post(`/estimates/${estimate.id}/consent`, {}, {
      preserveScroll: true,
      onFinish: () => setIssuingConsent(false),
    })
  }

  // "Charge Discovery rates" — only ever rendered for the login allowed to discount.
  const [applyingScheme, setApplyingScheme] = useState(false)
  const applySchemeRates = () => {
    if (!window.confirm(
      `Charge Discovery rates on this estimate?\n\n` +
      `${scheme_rates_preview} line(s) are above the Discovery rate and will come DOWN. ` +
      `Lines with no Discovery rate, or already below it, stay at the practice fee.\n\n` +
      `This is recorded against your name.`)) return
    setApplyingScheme(true)
    router.post(`/estimates/${estimate.id}/scheme-rates`, {}, {
      preserveScroll: true, onFinish: () => setApplyingScheme(false),
    })
  }

  const sendWhatsapp = () => {
    if (!window.confirm(`Send this ${as_invoice ? 'invoice' : 'estimate'} to ${patient.name || 'the patient'} on WhatsApp, with the PDF attached?`)) return
    setSendingWa(true)
    router.post(`/estimates/${estimate.id}/send_whatsapp`, {}, {
      preserveScroll: true,
      onSuccess: (page) => { const al = page?.props?.flash?.alert; al ? toast.error(al) : toast.success(`Sent to ${patient.name || 'the patient'} on WhatsApp`) },
      onError:   (errs) => toast.error(Object.values(errs || {})[0] || 'Could not send'),
      onFinish:  () => setSendingWa(false),
    })
  }
  // est2 (28 Oct 2026): a repeat send inside 10 min is refused; the desk can still send it again on purpose. The server
  // only offers this to admin / reception after a refusal, re-checks the role, and records it against the user.
  const sendAgain = (o) => {
    if (!window.confirm(`This estimate was already sent on ${o.label} ${o.ago}. Send it again anyway? This is recorded against your name.`)) return
    setSendingWa(true)
    router.post(`/estimates/${estimate.id}/send_${o.channel}`, { resend: '1' }, {
      preserveScroll: true,
      onSuccess: (page) => { const al = page?.props?.flash?.alert; al ? toast.error(al) : toast.success(page?.props?.flash?.notice || 'Sent again') },
      onError:   (errs) => toast.error(Object.values(errs || {})[0] || 'Could not send'),
      onFinish:  () => setSendingWa(false),
    })
  }
  // Phase 2B — send the interactive case page (annotated scans + phased plan + Accept + e-sign +
  // booking) over WhatsApp. Deliberate button press only, with a confirm — never automatic.
  const [sendingCase, setSendingCase] = useState(false)
  const sendCasePage = () => {
    if (!window.confirm(`Send an interactive case page for this ${as_invoice ? 'invoice' : 'estimate'} to ${patient.name || 'the patient'} on WhatsApp? They can view the plan, costs and scans, and accept online (accepting does NOT create an invoice).`)) return
    setSendingCase(true)
    router.post(`/estimates/${estimate.id}/send_case_page`, {}, {
      preserveScroll: true,
      onSuccess: (page) => { const al = page?.props?.flash?.alert; al ? toast.error(al) : toast.success(page?.props?.flash?.notice || `Case page sent to ${patient.name || 'the patient'} on WhatsApp`) },
      onError:   (errs) => toast.error(Object.values(errs || {})[0] || 'Could not send'),
      onFinish:  () => setSendingCase(false),
    })
  }
  const saveEstimate = () => {
    setSaving(true)
    router.patch(`/estimates/${estimate.id}`, { title: title.trim() }, {
      preserveScroll: true,
      // 🚨 "I just did the invoice for this patient but it did not save??" (Paul 2026-09-11). This button
      // only saves the DRAFT; the invoice is raised by "Create invoice" (accept_and_invoice). The old toast
      // said "Invoice saved — kept on record", so a draft that was never finalised read as a done invoice.
      onSuccess: () => toast.success(as_invoice ? 'Draft saved — click "Create invoice" to finalise it' : 'Estimate saved — kept on record'),
      onError:   (errs) => toast.error(Object.values(errs || {})[0] || 'Could not save'),
      onFinish:  () => setSaving(false),
    })
  }
  const aiCompose = () => {
    if (!aiPrompt.trim()) return
    setAiBusy(true)
    router.post(`/estimates/${estimate.id}/ai_compose`, { prompt: aiPrompt }, {
      preserveScroll: true,
      onSuccess: () => setAiPrompt(''),
      onFinish: () => setAiBusy(false),
    })
  }
  // Adjust the estimate IN PLACE from free-text feedback — edits the existing lines, never adds new ones.
  const reviseEstimate = () => {
    if (!revisePrompt.trim()) return
    setReviseBusy(true)
    router.post(`/estimates/${estimate.id}/revise_lines`, { instruction: revisePrompt }, {
      preserveScroll: true,
      onSuccess: () => setRevisePrompt(''),
      onFinish: () => setReviseBusy(false),
    })
  }
  // One-tap: re-read the FDI tooth numbers (tooth-only, keeps every code/qty/fee).
  const rereadTeeth = () => {
    setRereadBusy(true)
    router.post(`/estimates/${estimate.id}/reread_teeth`, {}, {
      preserveScroll: true,
      onFinish: () => setRereadBusy(false),
    })
  }
  const [dragOver, setDragOver]   = useState(false)
  // Patient view = hide tariff codes (plain-language only) for presenting to the patient.
  const [patientView, setPatientView] = useState(false)
  const isAccepted = estimate.status === 'accepted'
  // An accepted estimate is locked by default (the patient has agreed to it), but reception/the
  // dentist can deliberately re-open it to correct a code, fee or tooth. `unlockEdit` reveals the
  // same line-items editor used for drafts — the backend already permits the edit regardless of status.
  const [unlockEdit, setUnlockEdit] = useState(false)
  const showEditor = !isAccepted || unlockEdit
  // New-invoice flow only: the date the invoice will carry when finalised. Defaults to the
  // editor's date (today); reception can back-date it to when the work was actually done.
  const [invoiceDate, setInvoiceDate] = useState((estimate.date || todayISO()).slice(0, 10))

  // TICK-BOX PARTIAL INVOICING (Paul 2026-07-31): pick which codes to invoice for a specific day. Every
  // still-un-invoiced line defaults to ticked (so "Invoice selected" == the whole visit until you untick
  // one); an already-invoiced line is locked + links to its invoice, so a code can never be billed twice.
  const showInvoicePicker = !patientView && !as_invoice && !isDentist
  const invoiceableSig = (estimate.visits || []).flatMap((v) => (v.lines || []).filter((l) => !l.invoiced).map((l) => l.id)).join(',')
  const [selectedLines, setSelectedLines] = useState(new Set())
  useEffect(() => {
    setSelectedLines(new Set((estimate.visits || []).flatMap((v) => (v.lines || []).filter((l) => !l.invoiced).map((l) => l.id))))
  }, [invoiceableSig])
  const toggleLine = (id) => setSelectedLines((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })

  // ── Editable line items: add a tariff code (with tooth/qty/fee), remove a line ──
  const [draft, setDraft] = useState(EMPTY_LINE)
  const [codeQuery, setCodeQuery] = useState('')
  const [picked, setPicked] = useState(false)
  const codeInputRef = useRef(null)
  const allLines = (estimate.visits || []).flatMap((v) => v.lines || [])
  // 🚨 Place of service (Paul 2026-08-27). Set on the PLAN, because that is where a theatre case is
  // decided — pre-auth, sedation, day clinic — and it is carried into the invoice on conversion, so
  // nobody has to remember to re-key it at billing time. 11 rooms · 24 day clinic / day theatre ·
  // 21 admitted inpatient.
  const estimatePos = allLines[0]?.place_of_service || '11'
  const mixedPos    = new Set(allLines.map((l) => l.place_of_service || '11')).size > 1
  const setAllPos = (code) => {
    router.patch(`/estimates/${estimate.id}/place-of-service`, { place_of_service: code }, {
      preserveScroll: true,
      onSuccess: () => toast.success('Place of service updated on every line'),
      onError: () => toast.error('Could not set place of service'),
    })
  }
  // Editor rows in stable creation order so the "visit cascade" runs predictably top-to-bottom.
  const editorLines = [...allLines].sort((a, b) => (a.id || 0) - (b.id || 0))
  const visitOptions = Math.max(5, editorLines.reduce((m, l) => Math.max(m, l.visit || 1), 1) + 1)

  const codeMatches = (!picked && codeQuery.trim().length >= 1)
    ? procedure_codes.filter((c) => `${c.code} ${c.description}`.toLowerCase().includes(codeQuery.trim().toLowerCase())).slice(0, 8)
    : []

  const pickCode = (pc) => {
    setDraft({ ...draft, procedure_code_id: pc.id, code: pc.code, description: pc.description, fee: pc.fee != null ? String(pc.fee) : '' })
    setCodeQuery(`${pc.code} · ${pc.description}`)
    setPicked(true)
  }
  const addLine = () => {
    if (!draft.procedure_code_id && !draft.code && !draft.description) { toast.error('Pick a tariff code or type a description'); return }
    router.post(`/estimates/${estimate.id}/lines`, { estimate_line: draft }, {
      preserveScroll: true,
      // Reset the draft and put the cursor straight back in the code field so the
      // next line can be typed without reaching for the mouse (keyboard-first flow).
      onSuccess: () => { toast.success('Line added'); setDraft(EMPTY_LINE); setCodeQuery(''); setPicked(false); codeInputRef.current?.focus() },
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not add line'),
    })
  }

  // Enter in the code box: first Enter picks the top autocomplete match; once a code
  // is chosen (or a free-text description typed) Enter adds the line. Esc clears.
  const onCodeKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (codeMatches.length > 0) { pickCode(codeMatches[0]) } else { addLine() }
    } else if (e.key === 'Escape') {
      e.preventDefault(); setCodeQuery(''); setPicked(false); setDraft(EMPTY_LINE)
    }
  }
  // Enter in tooth/qty/fee adds the line immediately (cursor returns to the code box).
  const onFieldKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); addLine() } }

  // "Did you maybe forget a code?" nudges — dismissed ones hide for this session (until reload).
  const [dismissedCodes, setDismissedCodes] = useState([])
  const nudges = (companion_suggestions || []).filter((s) => !dismissedCodes.includes(s.code))

  // One-click add of a common code (the favourites chips) — no typing at all.
  const quickAdd = (pc) => {
    router.post(`/estimates/${estimate.id}/lines`, {
      estimate_line: { procedure_code_id: pc.id, code: pc.code, description: pc.description, tooth_number: '', quantity: '1', fee: pc.fee != null ? String(pc.fee) : '' },
    }, {
      preserveScroll: true,
      onSuccess: () => toast.success(`Added ${pc.code}`),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not add line'),
    })
  }

  // Apply a whole visit-bundle macro (adds all its codes as lines at once).
  const applyMacro = (id) => {
    if (!id) return
    router.post(`/estimates/${estimate.id}/apply_macro`, { treatment_macro_id: id }, {
      preserveScroll: true,
      onSuccess: () => toast.success('Visit bundle added'),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not add bundle'),
    })
  }
  const removeLine = (id) => {
    router.delete(`/estimate_lines/${id}`, { preserveScroll: true, onSuccess: () => toast.success('Line removed') })
  }

  // Edit an existing line in place (tooth / qty / fee / ICD-10). Sends the WHOLE line
  // with the one changed field merged in, so the controller's apply() never sees a
  // blank for an untouched column; the backend recalculates the totals after save.
  const patchLine = (l, changes, okMsg = 'Line updated') => {
    router.patch(`/estimate_lines/${l.id}`, {
      estimate_line: {
        procedure_code_id: l.procedure_code_id || '',
        code: l.code,
        description: l.description,
        tooth_number: l.tooth_number || '',
        quantity: l.quantity,
        fee: l.unit_fee,
        icd10_code: l.icd10_code || '',
        ...changes,
      },
    }, {
      preserveScroll: true,
      onSuccess: () => toast.success(okMsg),
      onError: () => toast.error('Could not update line'),
    })
  }
  // Inline CODE swap: the user retyped the tariff code on a line → resolve it to the new treatment +
  // fee (same catalogue the Quick-add uses) and patch THIS line, preserving tooth/visit/qty/ICD-10.
  const resolveCode = (l, pc) => {
    patchLine(l, {
      procedure_code_id: pc.id,
      code: pc.code,
      description: pc.description,
      fee: pc.fee != null ? String(pc.fee) : '',
    }, `Code changed to ${pc.code}`)
  }
  // Move THIS line to a different visit (per-line; the top grouping regroups automatically).
  const setVisitFrom = (l, visit) => {
    router.patch(`/estimate_lines/${l.id}`, {
      estimate_line: {
        procedure_code_id: l.procedure_code_id || '', code: l.code, description: l.description,
        tooth_number: l.tooth_number || '', quantity: l.quantity, fee: l.unit_fee, icd10_code: l.icd10_code || '',
        visit,
      },
    }, {
      preserveScroll: true,
      onSuccess: () => toast.success(`Moved to Visit ${visit}`),
      onError: () => toast.error('Could not set visit'),
    })
  }
  // Convert just ONE visit of this estimate into an invoice (editable after); rest of estimate stays.
  const invoiceVisit = (visit) => {
    if (!window.confirm(`Create an invoice for Visit ${visit}? You can edit it afterwards. The rest of the estimate stays as-is.`)) return
    router.post(`/estimates/${estimate.id}/invoice_visit`, { visit, invoice_date: invoiceDate }, {
      onSuccess: () => toast.success(`Visit ${visit} invoiced`),
      onError: () => toast.error('Could not invoice this visit'),
    })
  }
  // Invoice ONLY the ticked (still un-invoiced) codes in this visit — the rest of the estimate stays.
  const invoiceSelected = (visit) => {
    const vlines = ((estimate.visits || []).find((x) => String(x.visit) === String(visit))?.lines) || []
    const ids = vlines.filter((l) => !l.invoiced && selectedLines.has(l.id)).map((l) => l.id)
    if (!ids.length) { toast.error('Tick at least one code to invoice'); return }
    const amount = rand(vlines.filter((l) => ids.includes(l.id)).reduce((s, l) => s + (l.line_total || 0), 0))
    if (!window.confirm(`Invoice ${ids.length} selected code(s) (${amount})? You can edit the invoice after. The rest of the estimate stays.`)) return
    router.post(`/estimates/${estimate.id}/invoice_lines`, { line_ids: ids, invoice_date: invoiceDate }, {
      onSuccess: () => toast.success(`${ids.length} code(s) invoiced`),
      onError: () => toast.error('Could not invoice the selected codes'),
    })
  }

  const uploadFile = (file) => {
    const fd = new FormData()
    fd.append('file', file)
    router.post(`/estimates/${estimate.id}/upload_attachment`, fd, {
      preserveScroll: true,
      onSuccess: (page) => toast.success(page?.props?.flash?.notice || `Attached ${file.name}`),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Upload failed'),
      forceFormData: true,
    })
  }
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false)
    const files = Array.from(e.dataTransfer.files || [])
    files.forEach(uploadFile)
  }
  const onPick = (e) => {
    Array.from(e.target.files || []).forEach(uploadFile)
    e.target.value = '' // reset so the same file can be re-picked
  }
  const removeAttachment = (id) => {
    if (!confirm('Remove this attachment?')) return
    router.delete(`/estimates/${estimate.id}/attachments/${id}`, {
      preserveScroll: true,
      onSuccess: () => toast.success('Removed'),
    })
  }

  const acceptEstimate = () => {
    const amount = `R${(estimate.total || 0).toLocaleString('en-ZA')}`
    if (as_invoice) {
      // Deliberate "New invoice" flow — finalise into a real invoice.
      if (!confirm(`Create this ${amount} invoice? It will be finalised and can no longer be edited.`)) return
      setAccepting(true)
      router.post(`/estimates/${estimate.id}/accept_and_invoice`, { invoice_date: invoiceDate }, {
        onSuccess: (page) => toast.success(page?.props?.flash?.notice || 'Invoice created'),
        onError:   (errs) => toast.error(Object.values(errs || {})[0] || 'Could not create invoice'),
        onFinish:  () => setAccepting(false),
      })
      return
    }
    // Plain estimate — mark ACCEPTED ONLY. Does NOT create an invoice (Paul 2026-06-30).
    if (!confirm(`Mark this ${amount} estimate as accepted? This records that the patient agreed — it does NOT create an invoice. Use “Invoice this visit” when you’re ready to bill.`)) return
    setAccepting(true)
    router.post(`/estimates/${estimate.id}/accept`, {}, {
      onSuccess: (page) => toast.success(page?.props?.flash?.notice || 'Marked accepted'),
      onError:   (errs) => toast.error(Object.values(errs || {})[0] || 'Could not mark accepted'),
      onFinish:  () => setAccepting(false),
    })
  }

  // Print = print the SAME server-rendered PDF as "Download PDF" (not the HTML page, which the
  // browser styles badly). Load the PDF in a hidden iframe and fire its print dialog so the
  // printout is byte-identical to the downloaded document; fall back to opening it if a browser
  // blocks programmatic print of an embedded PDF.
  const printPdf = () => {
    const url = `/estimates/${estimate.id}.pdf`
    let frame = document.getElementById('estimate-pdf-print-frame')
    if (!frame) {
      frame = document.createElement('iframe')
      frame.id = 'estimate-pdf-print-frame'
      frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;'
      document.body.appendChild(frame)
    }
    frame.onload = () => {
      try { frame.contentWindow.focus(); frame.contentWindow.print() }
      catch { window.open(url, '_blank', 'noopener') }
    }
    frame.src = url
  }

  return (
    <DashboardLayout>
      <div className="mb-4 flex items-center justify-between no-print">
        <SmartBack fallback={patient?.id ? `/patients/${patient.id}` : '/estimates'} label="Back" />
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setPatientView((v) => !v)} className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border px-3 py-1.5 text-sm font-medium text-brand-ink hover:bg-brand-surface" title="Switch between the clinical view (with tariff codes) and a patient-friendly view (plain language, no codes)">
            {patientView ? 'Clinical view' : 'Patient view'}
          </button>
          <button onClick={printPdf} title="Print the estimate (same format as Download PDF)" className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border px-3 py-1.5 text-sm text-brand-ink hover:bg-brand-surface">
            <Printer size={14} /> Print
          </button>
          <a
            href={`/estimates/${estimate.id}.pdf?dl=1`}
            download={`${estimate.number || 'estimate'}.pdf`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border px-3 py-1.5 text-sm text-brand-ink hover:bg-brand-surface"
          >
            <Download size={14} /> Download PDF
          </a>
          <button
            onClick={sendWhatsapp}
            disabled={sendingWa || !patient.whatsapp_phone}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-sm font-semibold text-emerald-700 shadow-sm hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
            title={patient.whatsapp_phone ? 'Send this estimate to the patient on WhatsApp (PDF attached)' : 'No WhatsApp number on file for this patient — add one on their profile'}
          >
            <MessageCircle size={14} /> {sendingWa ? 'Sending…' : 'Send on WhatsApp'}
          </button>
          {(send_again || []).map((o) => (
            <button
              key={`send-again-${o.channel}`}
              onClick={() => sendAgain(o)}
              disabled={sendingWa}
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-400 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-900 shadow-sm hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
              title={`Not re-sent: already sent on ${o.label} ${o.ago}. Sends it again on purpose, recorded against your name.`}
            >
              Send again anyway ({o.label}, sent {o.ago})
            </button>
          ))}
          {!as_invoice && scheme_rates_allowed && (
            <button
              onClick={applySchemeRates}
              disabled={applyingScheme || scheme_rates_preview === 0}
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-400 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-900 shadow-sm hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
              title={scheme_rates_preview === 0
                ? 'Every line is already at or below the Discovery rate'
                : `Bring ${scheme_rates_preview} line(s) down to the Discovery rate`}
            >
              <Percent size={14} /> {applyingScheme ? 'Applying…' : `Charge Discovery rates${scheme_rates_preview ? ` (${scheme_rates_preview})` : ''}`}
            </button>
          )}
          {!as_invoice && consent_enabled && (
            <button
              onClick={issueConsent}
              disabled={issuingConsent}
              className="inline-flex items-center gap-1.5 rounded-lg border border-brand-primary bg-white px-3 py-1.5 text-sm font-semibold text-brand-primary shadow-sm hover:bg-brand-surface disabled:opacity-50"
              title="Create the treatment-plan consent from this estimate — the plan, the risks, the alternatives and the seven confirmations, for the patient to sign"
            >
              <ShieldCheck size={14} /> {issuingConsent ? 'Preparing…' : 'Consent form'}
            </button>
          )}
          {!as_invoice && (
            <button
              onClick={sendCasePage}
              disabled={sendingCase || !patient.whatsapp_phone}
              className="inline-flex items-center gap-1.5 rounded-lg border border-brand-primary bg-white px-3 py-1.5 text-sm font-semibold text-brand-primary shadow-sm hover:bg-brand-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              title={patient.whatsapp_phone ? 'Send an interactive case page (scans + plan + accept + booking). Accepting does NOT invoice.' : 'No WhatsApp number on file for this patient — add one on their profile'}
            >
              <MessageCircle size={14} /> {sendingCase ? 'Sending…' : 'Send case page'}
            </button>
          )}
          {!isAccepted && (
            <button
              onClick={saveEstimate}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-lg border border-brand-primary bg-white px-3 py-1.5 text-sm font-semibold text-brand-primary shadow-sm hover:bg-brand-primary hover:text-white disabled:opacity-50"
              title={as_invoice ? 'Save this DRAFT — the invoice itself is raised by "Create invoice"' : 'Save this estimate and keep it on record — does NOT create an invoice'}
            >
              <Save size={14} /> {saving ? 'Saving…' : (as_invoice ? 'Save draft' : 'Save estimate')}
            </button>
          )}
          {!isDentist && (
            <button
              onClick={acceptEstimate}
              disabled={isAccepted || accepting}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
              title={isAccepted ? 'Already accepted' : (as_invoice ? 'Finalise this as an invoice' : 'Mark the estimate as accepted — does NOT create an invoice')}
            >
              <ThumbsUp size={14} /> {isAccepted ? (as_invoice ? 'Invoiced' : 'Accepted') : (as_invoice ? 'Create invoice' : 'Accept estimate')}
            </button>
          )}
        </div>
      </div>

      {estimate.ai_note && !noteDismissed && (
        <div className="mx-auto mb-3 flex max-w-3xl items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 no-print">
          <Sparkles size={15} className="mt-0.5 shrink-0" />
          <span className="flex-1"><span className="font-semibold">AI note — please confirm:</span> {estimate.ai_note}</span>
          <button type="button" onClick={() => setNoteDismissed(true)} className="shrink-0 text-amber-600 hover:text-amber-800" title="Dismiss"><XIcon size={14} /></button>
        </div>
      )}

      <div className="print-document mx-auto max-w-3xl rounded-xl border border-brand-border bg-white p-8 shadow-sm">
        {/* Header — brand wordmark + practice identifiers */}
        <div className="flex items-start justify-between border-b border-brand-border pb-5">
          <div>
            <BrandLogo className="h-14" />
            <p className="mt-2 text-xs text-brand-muted">{practice.address}</p>
            <p className="text-xs text-brand-muted">{practice.phone} · {practice.email}</p>
            <p className="mt-2 text-xs text-brand-ink"><span className="text-brand-muted">HPCSA:</span> {practice.hpcsa} &nbsp; <span className="text-brand-muted">Practice no:</span> {practice.bhf} &nbsp; <span className="text-brand-muted">VAT:</span> {practice.vat_number}</p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-brand-muted">{as_invoice ? 'Invoice' : 'Estimate'}</p>
            <p className="font-mono text-sm font-semibold text-brand-ink">{estimate.number}</p>
            {estimate.origin === 'chart_ai'
              ? <span className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">AI · from chart</span>
              : estimate.ai
              ? <span className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">AI-assisted</span>
              : <span className="mt-1 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">Manual</span>}
            {as_invoice && !isAccepted ? (
              <div className="mt-1 flex items-center justify-end gap-1.5 text-xs text-brand-muted no-print">
                <span>Date:</span>
                <input type="date" value={invoiceDate} max={todayISO()} min={minISO()}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  title="The date this invoice will carry — back-date to when the work was done"
                  className="rounded-md border border-brand-border px-1.5 py-1 text-xs text-brand-ink" />
              </div>
            ) : (
              <p className="mt-1 text-xs text-brand-muted">Date: {fmtDate(as_invoice ? invoiceDate : estimate.date)}</p>
            )}
            {isAccepted && <p className="mt-1 text-xs font-medium text-emerald-600">Accepted</p>}
            {!isAccepted && estimate.valid_until && (() => {
              const days = Math.ceil((new Date(estimate.valid_until) - new Date(new Date().toDateString())) / 86400000)
              return days < 0
                ? <p className="mt-1 inline-block rounded-md bg-brand-danger/10 px-2 py-0.5 text-xs font-semibold text-brand-danger">Expired {fmtDate(estimate.valid_until)} — re-quote if proceeding</p>
                : <p className={`mt-1 text-xs ${days <= 7 ? 'font-medium text-amber-600' : 'text-brand-muted'}`}>Valid until {fmtDate(estimate.valid_until)}{days <= 7 ? ` · ${days} day${days === 1 ? '' : 's'} left` : ''}</p>
            })()}
          </div>
        </div>

        {/* Optional document title/label — controlled, saves on blur or via the Save button, and
            shows on screen + on the PDF + patient view. */}
        <div className="no-print border-b border-brand-border py-3">
          <label className="text-xs uppercase tracking-wide text-brand-muted">{as_invoice ? 'Invoice' : 'Estimate'} title (optional — shows on the document)</label>
          <input
            type="text" value={title} placeholder="e.g. Crown + filling treatment plan"
            onChange={(e) => setTitle(e.target.value)}
            onBlur={(e) => { const v = e.target.value.trim(); if (v !== (estimate.title || '')) router.patch(`/estimates/${estimate.id}`, { title: v }, { preserveScroll: true, onSuccess: () => toast.success('Title saved') }) }}
            className="mt-1 block w-full max-w-lg rounded-lg border border-brand-border px-3 py-1.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none"
          />
        </div>
        {title && <p className="mt-2 text-base font-semibold text-brand-ink">{title}</p>}
        {estimate.ai && estimate.status !== 'accepted' && (
          <p className="mt-2 inline-flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-sm font-bold text-red-700">
            ⚠ AI estimated — please review before accepting
          </p>
        )}
        {/* Accepted estimates are locked; reception can deliberately re-open to fix a code/fee/tooth. */}
        {isAccepted && !as_invoice && (
          <div className="no-print mt-2 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700">✓ Accepted</span>
            <button
              type="button"
              onClick={() => setUnlockEdit((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-brand-primary bg-white px-3 py-1.5 text-sm font-semibold text-brand-primary shadow-sm hover:bg-brand-primary hover:text-white"
            >
              {unlockEdit ? 'Done editing' : '✏️ Edit lines'}
            </button>
            {unlockEdit && <span className="text-xs text-brand-muted">Editing an accepted estimate — each change saves immediately.</span>}
          </div>
        )}

        {/* Follow-up tracking (estimates only — invoices go straight to the account, no follow-up). */}
        {!as_invoice && <FollowUpCard estimate={estimate} reasons={not_booked_reasons} />}

        {/* Patient + scheme */}
        <div className="grid grid-cols-2 gap-4 border-b border-brand-border py-4 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wide text-brand-muted">{as_invoice ? 'Invoice for' : 'Estimate for'}</p>
            <p className="font-medium text-brand-ink">{patient.name}</p>
            {estimate.account_code && <p className="text-brand-muted">Account: <span className="font-medium text-brand-ink">{estimate.account_code}</span>{estimate.account_relationship && estimate.account_relationship !== 'self' && <span className="ml-1 capitalize text-brand-muted">· {estimate.account_relationship}</span>}</p>}
            <p className="text-brand-muted">{patient.phone}</p>
            {patient.date_of_birth && <p className="text-brand-muted">DOB: {patient.date_of_birth}</p>}
            {patient.id_number && <p className="text-brand-muted">ID: {patient.id_number}</p>}
            {/* Treating dentist — carries through to the invoice on conversion (cycle 22) */}
            <div className="mt-2">
              <p className="text-xs uppercase tracking-wide text-brand-muted">Treating dentist</p>
              <p className="print-only font-medium text-brand-ink">{estimate.provider_name || '—'}</p>
              <select
                value={estimate.provider_name || ''}
                onChange={(e) => router.patch(`/estimates/${estimate.id}`, { provider_name: e.target.value }, { preserveScroll: true })}
                className="no-print mt-0.5 block w-full max-w-xs rounded-lg border border-brand-border px-2 py-1 text-sm text-brand-ink focus:border-brand-primary"
              >
                <option value="">— select dentist —</option>
                {providers.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              {estimate.last_seen_provider && (
                <p className="no-print mt-0.5 text-[11px] text-brand-muted">
                  Last saw: <span className="font-medium text-brand-ink">{estimate.last_seen_provider}</span>
                  {estimate.provider_name && estimate.provider_name !== estimate.last_seen_provider && ' · treating dentist changed from last-seen'}
                </p>
              )}
            </div>
            {/* Place of service — like the treating dentist, this carries through to the invoice on
                conversion, so a theatre case is coded ONCE here and bills correctly. Paul 2026-08-27. */}
            <div className="mt-2">
              <p className="text-xs uppercase tracking-wide text-brand-muted">Place of service</p>
              <p className="print-only font-medium text-brand-ink">{mixedPos ? 'mixed across lines' : estimatePos}</p>
              <select
                value={mixedPos ? '' : estimatePos}
                onChange={(e) => { if (e.target.value) setAllPos(e.target.value) }}
                title="Where this treatment will be done — sets every line, and carries into the invoice"
                className="no-print mt-0.5 block w-full max-w-xs rounded-lg border border-brand-border px-2 py-1 text-sm text-brand-ink focus:border-brand-primary"
              >
                {mixedPos && <option value="">— mixed across lines —</option>}
                {place_of_service_options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <p className="no-print mt-0.5 text-[11px] text-brand-muted">
                Sedation or GA in a hospital day theatre (home the same day) is <strong>24</strong>; an admitted
                patient is <strong>21</strong>. Prints next to every line so the scheme pays the right benefit.
              </p>
            </div>
          </div>
          {/* Medical aid — shown so the patient can submit THIS estimate to their own scheme for a
              benefit confirmation / pre-authorisation (the practice still does not claim on their behalf). */}
          <div>
            {patient.scheme ? (
              <>
                <p className="text-xs uppercase tracking-wide text-brand-muted">Medical aid (for your claim)</p>
                <p className="text-brand-ink">{patient.scheme}</p>
                {patient.scheme_plan && <p className="text-brand-muted">Plan: {patient.scheme_plan}</p>}
                {patient.member_number && <p className="text-brand-muted">Member no: {patient.member_number}</p>}
                {patient.dependent_code && <p className="text-brand-muted">Dependent code: {patient.dependent_code}</p>}
                {patient.main_member && <p className="text-brand-muted">Main member: {patient.main_member}</p>}
                {patient.main_member_id && <p className="text-brand-muted">Main member ID: {patient.main_member_id}</p>}
              </>
            ) : null}
          </div>
        </div>

        {/* Multi-visit explainer (replaces the confusing "phases") */}
        {multiVisit && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-brand-border bg-brand-surface px-3 py-2 text-xs text-brand-ink">
            <Info size={14} className="mt-0.5 flex-shrink-0 text-brand-primary" />
            <p>This treatment is planned over <strong>{estimate.visits.length} visits</strong> — each visit is a separate appointment. The grouping below shows what is planned for each visit.</p>
          </div>
        )}

        {/* Lines grouped by visit */}
        {(estimate.visits || []).map((v) => (
          <div key={v.visit} className="mt-4">
            <div className="mb-1 flex items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-brand-primary">{v.label || 'Treatment'}</p>
                {/* Internal chair-time for booking — NOT printed on the estimate/PDF. */}
                {!patientView && (
                  <label className="no-print flex items-center gap-1 text-xs text-brand-muted" title="Chair time this visit needs — used when booking, not shown on the estimate">
                    <Clock size={12} />
                    <input
                      type="number" min="0" step="5" defaultValue={v.minutes || ''} placeholder="min"
                      onBlur={(e) => router.patch(`/estimates/${estimate.id}`, { visit_minutes: { [v.visit]: e.target.value } }, { preserveScroll: true, onSuccess: () => toast.success(`Visit ${v.visit} time saved`) })}
                      className="w-16 rounded border border-brand-border px-1.5 py-0.5 text-xs text-brand-ink focus:border-brand-primary focus:outline-none"
                    />
                    <span>min</span>
                  </label>
                )}
              </div>
              {showInvoicePicker && (v.lines || []).some((l) => !l.invoiced) && (
                <button
                  onClick={() => invoiceSelected(v.visit)}
                  title="Invoice the ticked codes below (editable after) — the rest of the estimate stays for later"
                  className="rounded-lg border border-brand-primary px-2.5 py-1 text-xs font-semibold text-brand-primary transition-colors hover:bg-brand-primary hover:text-white">
                  {(() => {
                    const uninv = (v.lines || []).filter((l) => !l.invoiced)
                    const picked = uninv.filter((l) => selectedLines.has(l.id)).length
                    return picked === uninv.length ? 'Invoice this visit →' : `Invoice ${picked} selected →`
                  })()}
                </button>
              )}
            </div>
            <table className="w-full text-sm">
              <thead className="border-b border-brand-border text-left text-xs uppercase tracking-wide text-brand-muted">
                <tr>
                  {showInvoicePicker && <th className="w-6 py-1.5 no-print" title="Tick the codes to invoice for this day"></th>}
                  {!patientView && <th className="py-1.5 font-semibold">Code</th>}
                  <th className="py-1.5 font-semibold">Treatment</th>
                  <th className="py-1.5 text-center font-semibold">Tooth</th>
                  <th className="py-1.5 text-center font-semibold">Qty</th>
                  <th className="py-1.5 text-right font-semibold">Fee</th>
                  <th className="py-1.5 text-right font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody>
                {v.lines.map((l, i) => (
                  <tr key={i} className={`border-b border-brand-border/40${l.invoiced ? ' opacity-60' : ''}${l.baby_tooth_fee_wrong ? ' bg-red-50' : ''}`}>
                    {showInvoicePicker && (
                      <td className="py-1.5 no-print align-middle">
                        {l.invoiced ? (
                          <a href={`/invoices/${l.invoice_id}`} title="Already invoiced — open the invoice" onClick={(e) => e.stopPropagation()} className="text-xs font-semibold text-brand-primary hover:underline">✓</a>
                        ) : (
                          <input type="checkbox" checked={selectedLines.has(l.id)} onChange={() => toggleLine(l.id)} title="Tick to include this code when you invoice this visit" className="h-3.5 w-3.5 accent-brand-primary align-middle" />
                        )}
                      </td>
                    )}
                    {!patientView && <td className="py-1.5 font-mono text-brand-ink">{l.code}</td>}
                    <td className="py-1.5 text-brand-ink">
                      {l.description}
                      {l.icd10_code && <span className="ml-2 text-xs text-brand-muted">ICD-10: {l.icd10_code}</span>}
                      {l.baby_tooth_fee_wrong && <span className="ml-2 rounded bg-red-600 px-1 text-[10px] font-bold text-white" title="Baby-tooth extraction (code 8201 on a primary tooth) must be R250">⚠ SHOULD BE R250</span>}
                    </td>
                    <td className="py-1.5 text-center text-brand-muted">{l.tooth_number || '—'}</td>
                    <td className="py-1.5 text-center text-brand-ink">{l.quantity}</td>
                    <td className={`py-1.5 text-right${l.baby_tooth_fee_wrong ? ' font-bold text-red-600' : ' text-brand-muted'}`}>{rand(l.unit_fee)}</td>
                    <td className={`py-1.5 text-right${l.baby_tooth_fee_wrong ? ' font-bold text-red-600' : ' text-brand-ink'}`}>{rand(l.line_total)}</td>
                  </tr>
                ))}
              </tbody>
              {multiVisit && (
                <tfoot>
                  <tr className="border-t border-brand-border">
                    <td colSpan={(patientView ? 4 : 5) + (showInvoicePicker ? 1 : 0)} className="py-1.5 text-right text-xs font-semibold uppercase tracking-wide text-brand-muted">{v.label || 'Treatment'} subtotal</td>
                    <td className="py-1.5 text-right text-sm font-bold text-brand-ink">{rand(v.lines.reduce((s, l) => s + (l.line_total || 0), 0))}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        ))}

        {/* Totals — one amount the patient pays. The practice does NOT split or claim from medical aid. */}
        {/* When a discount is set the patient sees the FULL price, the discount, and the total payable. */}
        <div className="mt-4 ml-auto w-72 space-y-1 text-sm">
          {estimate.vat > 0 && (
            <div className="flex justify-between text-brand-muted"><span>VAT (15%, incl.)</span><span>{rand(estimate.vat)}</span></div>
          )}
          {estimate.discounted ? (
            <>
              <div className="flex justify-between text-brand-muted"><span>Subtotal (full price)</span><span>{rand(estimate.total)}</span></div>
              <div className="flex justify-between font-medium text-emerald-700">
                <span>Discount{estimate.discount_label ? ` (${estimate.discount_label})` : ''}</span>
                <span>&minus; {rand(estimate.discount)}</span>
              </div>
              <div className="mt-1 flex items-baseline justify-between border-t-2 border-brand-primary pt-2">
                <span className="font-semibold text-brand-ink">Total payable (incl. VAT)</span>
                <span className="text-xl font-bold text-brand-primary">{rand(estimate.net_total)}</span>
              </div>
            </>
          ) : (
            <div className="mt-1 flex items-baseline justify-between border-t-2 border-brand-primary pt-2">
              <span className="font-semibold text-brand-ink">Total — amount payable (incl. VAT)</span>
              <span className="text-xl font-bold text-brand-primary">{rand(estimate.total)}</span>
            </div>
          )}
        </div>

        {/* Admin-only discount control (Paul 2026-08-31) — only an admin sees it, never the patient view. */}
        {estimate.can_discount && !patientView && <DiscountControl estimate={estimate} />}

        <div className="mt-6 border-t border-brand-border pt-4 text-xs text-brand-muted">
          <p className="font-medium text-brand-ink">Payment / banking</p>
          <p>{practice.bank}</p>
          <p className="mt-3 font-semibold text-brand-ink">ESTIMATE VALID FOR 30 DAYS{estimate.valid_until ? ` (until ${fmtDate(estimate.valid_until)})` : ''}</p>
          <p className="mt-1">This is an estimate of planned treatment, not a final account. Actual fees may vary depending on what is clinically required on the day, and additional procedures may be needed.</p>
          <p className="mt-1">You remain personally liable for all fees for treatment rendered, irrespective of your medical scheme membership or benefits. It is your responsibility to confirm your available benefits and obtain any required pre-authorisation from your scheme before treatment. You are welcome to submit this document, with its procedure and ICD-10 codes, to your own medical scheme — the practice does not claim on your behalf.</p>
        </div>
      </div>

      {/* Edit line items — add a tariff code (tooth / qty / fee) or remove a line */}
      {showEditor && (
        <div className="no-print mx-auto mt-4 max-w-3xl rounded-xl border border-brand-border bg-white p-5">
          <h3 className="mb-1 text-sm font-semibold text-brand-ink">Line items</h3>
          <p className="mb-3 text-[11px] text-brand-muted">Tip: retype the <span className="font-mono">Code</span> on any line and press Enter to swap it — the treatment name &amp; fee update automatically and the tooth stays put.</p>
          {/* Shared code list so the inline Code editor autocompletes against the full catalogue. */}
          <datalist id="est-code-list">
            {procedure_codes.map((c) => <option key={c.id} value={c.code}>{c.code} — {c.description}</option>)}
          </datalist>
          {allLines.length === 0 ? (
            <p className="mb-3 text-sm text-brand-muted">No line items yet — add tariff codes below.</p>
          ) : (
            <table className="mb-4 w-full text-sm">
              <thead className="border-b border-brand-border text-left text-[11px] uppercase tracking-wide text-brand-muted">
                <tr>
                  <th className="py-1 font-semibold">Code</th>
                  <th className="py-1 font-semibold">Treatment</th>
                  <th className="py-1 text-center font-semibold">Visit</th>
                  <th className="py-1 text-center font-semibold" title="Place of service — where this item is performed (for the medical-aid claim)">POS</th>
                  <th className="py-1 text-center font-semibold">Tooth</th>
                  <th className="py-1 text-center font-semibold">Qty</th>
                  <th className="py-1 text-right font-semibold">Fee</th>
                  <th className="py-1 text-center font-semibold">ICD-10</th>
                  <th className="py-1 text-right font-semibold">Amount</th>
                  <th className="py-1" aria-label="actions"></th>
                </tr>
              </thead>
              <tbody>
                {editorLines.map((l) => (
                  <tr key={l.id} className="border-b border-brand-border/40 align-top">
                    <td className="py-1.5">
                      <CodeEdit line={l} codes={procedure_codes} onResolve={resolveCode} listId="est-code-list" />
                    </td>
                    <td className="py-1.5 text-brand-ink">
                      {l.description}
                      {(l.flags || []).length > 0 && (
                        <span className="ml-2 inline-flex flex-wrap gap-1 align-middle">
                          {l.flags.includes('needs_tooth') && <span title="This procedure needs a tooth number — add it in the Tooth column" className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">⚠ add tooth</span>}
                          {l.flags.includes('enter_fee') && <span title="No fee yet — enter the amount (e.g. the lab's quoted price for 8099)" className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">⚠ enter fee</span>}
                          {l.flags.includes('preauth') && <span title="Major work — most medical schemes require PRE-AUTHORISATION before treatment" className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700">🔒 pre-auth</span>}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 text-center">
                      <select
                        value={l.visit || 1}
                        onChange={(e) => setVisitFrom(l, parseInt(e.target.value, 10))}
                        title="Move this line to a visit — only this line changes; the grouping above updates"
                        className="rounded border border-brand-border px-1 py-0.5 text-xs"
                      >
                        {Array.from({ length: visitOptions }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5 text-center">
                      <select
                        value={l.place_of_service || '11'}
                        onChange={(e) => patchLine(l, { place_of_service: e.target.value }, 'Place of service updated')}
                        title="Place of service for this line (11 rooms · 24 day clinic / day theatre · 21 inpatient)"
                        className={`w-16 rounded border border-brand-border px-1 py-0.5 text-center text-xs${(l.place_of_service || '11') !== '11' ? ' font-semibold text-brand-ink' : ''}`}
                      >
                        {place_of_service_options.map((o) => <option key={o.value} value={o.value} title={o.label}>{o.value}</option>)}
                      </select>
                    </td>
                    <td className="py-1.5 text-center">
                      <input
                        key={`tooth-${l.id}-${l.tooth_number || ''}`}
                        defaultValue={l.tooth_number || ''}
                        onBlur={(e) => {
                          const v = e.target.value.trim()
                          if (v === (l.tooth_number || '')) return
                          patchLine(l, { tooth_number: v }, 'Tooth updated')
                        }}
                        placeholder="—"
                        title="Tooth number (FDI notation, e.g. 11 / 36)"
                        className="w-14 rounded border border-brand-border px-1.5 py-0.5 text-center text-xs"
                      />
                    </td>
                    <td className="py-1.5 text-center">
                      <input
                        key={`qty-${l.id}-${l.quantity}`}
                        defaultValue={l.quantity}
                        inputMode="numeric"
                        onBlur={(e) => {
                          const v = e.target.value.trim()
                          if (v === String(l.quantity)) return
                          patchLine(l, { quantity: v }, 'Quantity updated')
                        }}
                        title="Quantity / units"
                        className="w-12 rounded border border-brand-border px-1.5 py-0.5 text-center text-xs"
                      />
                    </td>
                    <td className="py-1.5 text-right">
                      <input
                        key={`fee-${l.id}-${l.unit_fee}`}
                        defaultValue={l.unit_fee}
                        inputMode="decimal"
                        onBlur={(e) => {
                          const v = e.target.value.trim()
                          if (v === String(l.unit_fee)) return
                          patchLine(l, { fee: v }, 'Fee updated')
                        }}
                        title="Fee per unit (incl. VAT)"
                        className="w-20 rounded border border-brand-border px-1.5 py-0.5 text-right text-xs"
                      />
                    </td>
                    <td className="py-1.5 text-center">
                      <input
                        key={`icd-${l.id}-${l.icd10_code || ''}`}
                        defaultValue={l.icd10_code || ''}
                        onBlur={(e) => {
                          const v = e.target.value.trim().toUpperCase()
                          if (v === (l.icd10_code || '')) return
                          patchLine(l, { icd10_code: v }, 'ICD-10 updated')
                        }}
                        placeholder="ICD-10"
                        title="ICD-10 diagnosis code for this line (for the patient's medical-aid claim)"
                        className="w-20 rounded border border-brand-border px-1.5 py-0.5 text-center text-xs uppercase"
                      />
                    </td>
                    <td className="py-1.5 text-right text-brand-ink">{rand(l.line_total)}</td>
                    <td className="py-1.5 pl-2 text-right">
                      <button onClick={() => removeLine(l.id)} title="Remove line" className="rounded p-1 text-brand-muted hover:bg-brand-surface hover:text-brand-danger"><XIcon size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {suggested_macros.length > 0 && (
            <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 no-print">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                Suggested for this visit{visit_reason ? <span className="font-normal normal-case"> — “{visit_reason}”</span> : null} · please review
              </p>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {suggested_macros.map((m) => (
                  <button key={m.id} type="button" onClick={() => applyMacro(m.id)}
                    title={`Add the ${m.name} bundle (review the lines after)`}
                    className="rounded-full border border-amber-400 bg-white px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100">
                    + {m.name}
                  </button>
                ))}
                <span className="text-[11px] text-amber-700/80">AI-suggested from the booking — check before sending.</span>
              </div>
            </div>
          )}

          {showEditor && (
            <div className="mb-3 rounded-xl border border-brand-primary/30 bg-brand-primary/5 p-3 no-print">
              <label className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-primary">
                <Sparkles size={13} /> Describe the treatment — AI fills in the codes
              </label>
              <div className="flex gap-2">
                <input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); aiCompose() } }}
                  placeholder='e.g. "two crowns on tooth 21 and 11" or "root canal on 34"'
                  className="flex-1 rounded-lg border border-brand-border px-3 py-2 text-sm focus:border-brand-primary focus:outline-none" />
                <button type="button" onClick={aiCompose} disabled={aiBusy || !aiPrompt.trim()}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-brand-primary px-3 py-2 text-sm font-semibold text-white hover:brightness-95 disabled:opacity-50">
                  <Sparkles size={14} /> {aiBusy ? 'Thinking…' : 'Auto-fill codes'}
                </button>
              </div>
              <p className="mt-1 text-[11px] text-brand-muted">Uses your code catalogue + treatment bundles — one line per tooth. Review &amp; adjust after.</p>
            </div>
          )}

          {/* Adjust IN PLACE (Paul 2026-07-25) — edits the existing lines from feedback, never adds new ones. */}
          {showEditor && (estimate.visits || []).some((v) => (v.lines || []).length > 0) && (
            <div className="mb-3 rounded-xl border border-emerald-300/60 bg-emerald-50/60 p-3 no-print">
              <label className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                <Sparkles size={13} /> Adjust this estimate with AI — edits the lines, doesn’t add new ones
              </label>
              <div className="flex gap-2">
                <textarea value={revisePrompt} onChange={(e) => setRevisePrompt(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); reviseEstimate() } }}
                  rows={2}
                  placeholder='e.g. "the crown is on tooth 21, not 11" or "change the filling description to two surfaces"'
                  className="flex-1 resize-y rounded-lg border border-brand-border px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none" />
                <button type="button" onClick={reviseEstimate} disabled={reviseBusy || !revisePrompt.trim()}
                  className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:brightness-95 disabled:opacity-50">
                  <Sparkles size={14} /> {reviseBusy ? 'Adjusting…' : 'Adjust estimate'}
                </button>
              </div>
              <div className="mt-1.5 flex items-center justify-between gap-2">
                <p className="text-[11px] text-brand-muted">Adjusts teeth &amp; wording on the existing lines. It won’t change tariff codes or fees on a fixed-price package.</p>
                <button type="button" onClick={rereadTeeth} disabled={rereadBusy || reviseBusy}
                  title="Re-read the tooth numbers from each line’s description — changes teeth only, keeps every code, quantity and fee."
                  className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-emerald-400 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
                  {rereadBusy ? 'Re-reading…' : '↻ Re-read teeth'}
                </button>
              </div>
            </div>
          )}

          {(favourite_codes.length > 0 || treatment_macros.length > 0) && (
            <div className="mb-2 flex flex-wrap items-center gap-1.5 no-print">
              {favourite_codes.length > 0 && <span className="text-[11px] font-medium uppercase tracking-wide text-brand-muted">Quick add</span>}
              {favourite_codes.map((c) => (
                <button key={c.id} type="button" onClick={() => quickAdd(c)}
                  title={`${c.description}${c.fee != null ? ` · ${rand(c.fee)}` : ''}`}
                  className="rounded-full border border-brand-border bg-brand-surface px-2.5 py-1 text-xs font-medium text-brand-ink hover:border-brand-primary hover:bg-white">
                  <span className="font-mono">{c.code}</span>
                </button>
              ))}
              {treatment_macros.length > 0 && (
                <select value="" onChange={(e) => { applyMacro(e.target.value); e.target.value = '' }}
                  title="Add a whole visit bundle (all its codes) in one click"
                  className="ml-1 rounded-full border border-brand-border bg-white px-2.5 py-1 text-xs font-medium text-brand-ink hover:border-brand-primary">
                  <option value="">+ Visit bundle…</option>
                  {treatment_macros.map((m) => (<option key={m.id} value={m.id}>{m.code} · {m.name}</option>))}
                </select>
              )}
            </div>
          )}

          {nudges.length > 0 && (
            <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 no-print">
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-amber-800">
                <span aria-hidden>⚠</span> Did you maybe forget a code?
              </div>
              <ul className="space-y-1.5">
                {nudges.map((s) => (
                  <li key={s.code} className="flex flex-wrap items-center justify-between gap-2 text-xs text-amber-900">
                    <span className="min-w-0 flex-1">
                      <span className="font-mono font-semibold">{s.code}</span> {s.description}
                      {s.fee != null && <span className="text-amber-700"> · {rand(s.fee)}</span>}
                      <span className="block text-[11px] text-amber-700">{s.reason}</span>
                    </span>
                    <span className="flex shrink-0 gap-1">
                      <button type="button" onClick={() => quickAdd({ ...s, id: s.procedure_code_id })}
                        className="rounded border border-emerald-400 bg-white px-2 py-0.5 font-semibold text-emerald-700 hover:bg-emerald-50">+ Add</button>
                      <button type="button" onClick={() => setDismissedCodes((d) => [...d, s.code])}
                        className="rounded border border-amber-300 bg-white px-2 py-0.5 font-medium text-amber-700 hover:bg-amber-100">Dismiss</button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-12">
            <div className="relative sm:col-span-6">
              <input
                ref={codeInputRef}
                value={codeQuery}
                onChange={(e) => { setCodeQuery(e.target.value); setPicked(false); setDraft({ ...draft, procedure_code_id: '', code: '' }) }}
                onKeyDown={onCodeKey}
                placeholder="Search tariff code or treatment…  (Enter to pick / add)"
                className={editCls}
              />
              {codeMatches.length > 0 && (
                <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-brand-border bg-white shadow-lg">
                  {codeMatches.map((c) => (
                    <button key={c.id} type="button" onClick={() => pickCode(c)} className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-brand-surface">
                      <span className="truncate"><span className="font-mono text-brand-ink">{c.code}</span> · {c.description}</span>
                      <span className="whitespace-nowrap text-brand-muted">{c.fee != null ? rand(c.fee) : '—'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <input value={draft.tooth_number} onChange={(e) => setDraft({ ...draft, tooth_number: e.target.value })} onKeyDown={onFieldKey} placeholder="Tooth" className={`${editCls} sm:col-span-2`} />
            <input value={draft.quantity} onChange={(e) => setDraft({ ...draft, quantity: e.target.value })} onKeyDown={onFieldKey} placeholder="Qty" inputMode="numeric" className={`${editCls} sm:col-span-1`} />
            <input value={draft.fee} onChange={(e) => setDraft({ ...draft, fee: e.target.value })} onKeyDown={onFieldKey} placeholder="Fee incl VAT" inputMode="decimal" className={`${editCls} sm:col-span-2`} />
            <button onClick={addLine} className="rounded-md bg-brand-primary px-3 py-2 text-sm font-medium text-white hover:brightness-95 sm:col-span-1">Add</button>
          </div>
          <p className="mt-1 text-[11px] text-brand-muted no-print">Type a code or treatment, press <kbd className="rounded border border-brand-border px-1">Enter</kbd> to pick it, <kbd className="rounded border border-brand-border px-1">Enter</kbd> again to add the line — the cursor returns here for the next one. <kbd className="rounded border border-brand-border px-1">Esc</kbd> clears.</p>
          <p className="mt-2 text-xs text-brand-muted">Pick a code to auto-fill the fee (editable). Tooth + quantity optional. Totals update automatically.</p>
        </div>
      )}

      {/* C1 — Drag-drop attachments (screen only, hidden on print) */}
      <div className="no-print mx-auto mt-4 max-w-3xl">
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${
            dragOver ? 'border-brand-primary bg-brand-primary/5' : 'border-brand-border bg-white'
          }`}
        >
          <UploadCloud size={22} className="mx-auto mb-1 text-brand-muted" />
          <p className="text-sm text-brand-ink">
            Drag X-ray screenshots, intra-oral photos, or reference PDFs here
          </p>
          <label className="mt-2 inline-flex cursor-pointer items-center gap-1 text-xs font-semibold text-brand-primary hover:underline">
            or browse files
            <input type="file" multiple onChange={onPick} className="hidden" />
          </label>
        </div>

        {attachments.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {attachments.map((a) => (
              <li key={a.id} className="flex items-center justify-between rounded-lg border border-brand-border bg-white px-3 py-2 text-sm">
                <a href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-brand-ink hover:underline">
                  <Paperclip size={13} className="text-brand-muted" />
                  <span>{a.filename}</span>
                  <span className="text-xs text-brand-muted">· {(a.byte_size / 1024).toFixed(1)} KB</span>
                </a>
                <button onClick={() => removeAttachment(a.id)} title="Remove"
                  className="rounded-md p-1 text-brand-muted hover:bg-brand-surface hover:text-brand-danger">
                  <XIcon size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </DashboardLayout>
  )
}

// Admin-only whole-estimate discount control (Paul 2026-08-31). Enter a % or a rand amount + optional
// reason; posts to /estimates/:id/discount. Only ever rendered when estimate.can_discount is true.
function DiscountControl({ estimate }) {
  const [open, setOpen] = useState(!!estimate.discounted)
  const [kind, setKind] = useState(estimate.discount_kind || 'percent')
  const [pct, setPct]   = useState(estimate.discount_kind === 'percent' ? String((estimate.discount_value || 0) / 100) : '')
  const [amt, setAmt]   = useState(estimate.discount_kind === 'amount' ? String((estimate.discount_value || 0) / 100) : '')
  const [why, setWhy]   = useState(estimate.discount_reason || '')

  const apply = () => {
    const payload = { discount_kind: kind, discount_reason: why }
    if (kind === 'percent') payload.discount_percent = pct
    else payload.discount_amount = amt
    router.post(`/estimates/${estimate.id}/discount`, payload, { preserveScroll: true })
  }
  const remove = () => router.post(`/estimates/${estimate.id}/discount`, { discount_kind: '' }, { preserveScroll: true })

  if (!open) {
    return (
      <div className="mt-3 ml-auto w-72 text-right">
        <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:underline">
          <Percent className="h-3 w-3" /> Apply a discount
        </button>
      </div>
    )
  }
  return (
    <div className="mt-3 ml-auto w-72 rounded-lg border border-dashed border-brand-primary/50 bg-brand-primary/5 p-3 text-sm">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-brand-ink"><Percent className="h-3.5 w-3.5" /> Discount (admin only)</div>
      <div className="mb-2 flex gap-1">
        <button type="button" onClick={() => setKind('percent')} className={`flex-1 rounded px-2 py-1 text-xs ${kind === 'percent' ? 'bg-brand-primary text-white' : 'border bg-white text-brand-muted'}`}>Percent %</button>
        <button type="button" onClick={() => setKind('amount')} className={`flex-1 rounded px-2 py-1 text-xs ${kind === 'amount' ? 'bg-brand-primary text-white' : 'border bg-white text-brand-muted'}`}>Rand R</button>
      </div>
      {kind === 'percent' ? (
        <input type="number" min="0" max="100" step="0.5" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="e.g. 10 (%)" className="mb-2 w-full rounded border px-2 py-1 text-sm" />
      ) : (
        <input type="number" min="0" step="50" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="e.g. 2000 (R)" className="mb-2 w-full rounded border px-2 py-1 text-sm" />
      )}
      <input type="text" value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Reason (optional)" className="mb-2 w-full rounded border px-2 py-1 text-sm" />
      <div className="flex gap-2">
        <button type="button" onClick={apply} className="flex-1 rounded bg-brand-primary px-2 py-1.5 text-xs font-semibold text-white hover:opacity-90">Apply discount</button>
        {estimate.discounted && <button type="button" onClick={remove} className="rounded border px-2 py-1.5 text-xs text-brand-muted hover:bg-white">Remove</button>}
      </div>
    </div>
  )
}
